import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Modal } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import InputField from 'components/InputField';
import validations from 'validations';
import api from 'api';
import { LISTING_GOING_CONCERN } from 'models/Listing';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { getDataRoomRoute } from 'utils';
import { useNavigate } from 'react-router-dom';
import { INDUSTRY } from 'models/Industry';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import AddressSearchField from 'components/AddressSearchField';

import './CreateNewDataRoomModal.styles.scss';

type CreateNewDataRoomModalProps = {
  className?: string;
} & ModalProps;

const CreateNewDataRoomModal: React.ForwardRefRenderFunction<
  ModalRef,
  CreateNewDataRoomModalProps
> = (props, ref) => {
  const { className, ...rest } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const navigate = useNavigate();

  const { popServerError } = useContext(PopNotificationsContext);
  const { isRWCMember } = useContext(CurrentUserContext);

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('create-new-data-room-modal', className);

  const { t } = useTranslation();

  const onSubmit = useCallback(
    async ({ name }: { name: string }) => {
      try {
        const {
          data: { _id },
        } = await api.dealRoom.createInitialDeal({
          type: LISTING_GOING_CONCERN,
        });

        const {
          data: { dataRoomId },
        } = await api.dealRoom.publishDeal(_id, {
          name,
          industry: INDUSTRY.OTHER,
        });

        navigate(getDataRoomRoute(dataRoomId));
        modal?.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [modal, popServerError, navigate],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('newDataRoom')}
      {...rest}
    >
      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting, invalid }) => (
          <form onSubmit={handleSubmit}>
            <Field
              name="name"
              component={isRWCMember ? AddressSearchField : InputField}
              label={t(isRWCMember ? 'address' : 'dataRoomName')}
              required
              validate={validations.required()}
            />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>

              <Button type="submit" disabled={submitting || invalid}>
                {t('createDataRoom')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(CreateNewDataRoomModal);
